/**
 * WelcomePanel - landing content shown when no tool is selected.
 * Background: stylized UTM zone grid of Indonesia (46N..54S), with brand-tinted
 * vertical lines that fade to transparent against the panel background.
 */

import { useEffect, useState } from "react";
import { Shield, Zap, Lock, Globe } from "lucide-react";
import { t } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";

// UTM zones covering Indonesia: 46..54 (longitudes 90E..144E)
const UTM_ZONES = Array.from({ length: 9 }, (_, i) => 46 + i);

// Indonesia silhouette - single flowing path that suggests the archipelago,
// aligned to the UTM grid extent (90°E..144°E mapped to viewBox 0..600).
// Kept abstract so it merges into the brand gradient instead of competing with it.
const INDONESIA_PATH = `
M 28 145
C 35 138, 55 130, 72 135 L 110 142
C 130 145, 155 155, 168 162
L 190 168
C 215 162, 240 158, 270 165
L 320 168 L 360 170
C 388 175, 410 178, 432 168
L 470 152
C 495 145, 525 148, 548 158
L 572 165
M 145 175
C 168 188, 195 198, 220 190
L 250 178
M 360 200
C 380 215, 405 225, 425 218
L 460 205
M 470 175
C 488 188, 505 198, 525 192
`.trim();

const VALUE_PROPS = [
  { Icon: Zap,    titleKey: "welcome.card.fast",    descKey: "welcome.card.fast.desc" },
  { Icon: Lock,   titleKey: "welcome.card.private", descKey: "welcome.card.private.desc" },
  { Icon: Globe,  titleKey: "welcome.card.offline", descKey: "welcome.card.offline.desc" },
  { Icon: Shield, titleKey: "welcome.card.free",    descKey: "welcome.card.free.desc" },
] as const;

const ROTATE_MS = 3600;

/**
 * The four selling points, one at a time on a single line.
 *
 * They used to sit in a 2×2 card grid, which held its shape on a desktop panel
 * but ate 259px of an 844px phone screen — space the tool wheel below it needed
 * and did not get. One line reads the same at any width, and each claim gets
 * read on its own instead of competing with three others.
 *
 * All four stay in the DOM for assistive tech; only one is shown at a time.
 */
const RotatingClaim = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % VALUE_PROPS.length), ROTATE_MS);
    return () => clearInterval(timer);
  }, [paused]);

  const { Icon, titleKey, descKey } = VALUE_PROPS[index];

  return (
    <div
      className="welcome-claim"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* key on the index restarts the fade for every claim */}
      <div key={index} className="welcome-claim-line">
        <span className="welcome-claim-icon">
          <Icon style={{ width: 13, height: 13 }} aria-hidden="true" />
        </span>
        <span className="welcome-claim-title">{t(titleKey)}</span>
        <span className="welcome-claim-sep" aria-hidden="true">·</span>
        <span className="welcome-claim-text">{t(descKey)}</span>
      </div>

      {/* Progress pips double as a manual selector */}
      <div className="welcome-claim-pips">
        {VALUE_PROPS.map((p, i) => (
          <button
            key={p.titleKey}
            onClick={() => { setIndex(i); setPaused(true); }}
            aria-label={t(p.titleKey)}
            aria-current={i === index}
            className={`welcome-claim-pip${i === index ? " is-active" : ""}`}
          />
        ))}
      </div>

      {/* Every claim, for screen readers and anything that does not animate */}
      <ul className="sr-only">
        {VALUE_PROPS.map((p) => (
          <li key={p.titleKey}>{t(p.titleKey)} — {t(p.descKey)}</li>
        ))}
      </ul>
    </div>
  );
};

const WelcomePanel = () => {
  useLocale(); // re-render on language change
  return (
  <div className="welcome-root">

    {/* ── Background: UTM grid of Indonesia ── */}
    <svg
      className="welcome-bg"
      viewBox="0 0 600 280"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="utm-fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="hsl(var(--brand))" stopOpacity="0.18" />
          <stop offset="55%"  stopColor="hsl(var(--brand))" stopOpacity="0.07" />
          <stop offset="100%" stopColor="hsl(var(--brand))" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="bg-fade" cx="50%" cy="40%" r="70%">
          <stop offset="0%"   stopColor="hsl(var(--background))" stopOpacity="0" />
          <stop offset="100%" stopColor="hsl(var(--background))" stopOpacity="0.95" />
        </radialGradient>
      </defs>

      {/* Latitude lines (horizontal, faint) */}
      {Array.from({ length: 5 }, (_, i) => (
        <line key={`lat-${i}`} x1="0" x2="600" y1={56 * (i + 1)} y2={56 * (i + 1)}
              stroke="hsl(var(--brand))" strokeOpacity="0.05" strokeWidth="0.5" />
      ))}

      {/* UTM zone vertical lines + zone numbers */}
      {UTM_ZONES.map((zone, i) => {
        // zone 46 starts at lon 90E, each zone is 6° wide; viewBox is 600 wide for 54° range
        const x = ((i) * 600) / (UTM_ZONES.length);
        return (
          <g key={zone} className="welcome-zone" style={{ animationDelay: `${i * 0.18}s` }}>
            <line x1={x} x2={x} y1="0" y2="280"
                  stroke="hsl(var(--brand))" strokeOpacity="0.22" strokeWidth="0.8"
                  strokeDasharray="3 4" />
            <text x={x + 4} y="16"
                  fill="hsl(var(--brand))" fillOpacity="0.45"
                  fontFamily="ui-monospace, monospace" fontWeight="700" fontSize="9">
              {zone}
            </text>
          </g>
        );
      })}
      {/* closing zone line */}
      <line x1="600" x2="600" y1="0" y2="280" stroke="hsl(var(--brand))" strokeOpacity="0.22" strokeWidth="0.8" strokeDasharray="3 4" />

      {/* Indonesia silhouette - single flowing coastline aligned to the UTM grid */}
      <path
        d={INDONESIA_PATH}
        fill="none"
        stroke="hsl(var(--brand))"
        strokeOpacity="0.55"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="welcome-coast"
      />

      {/* Fade-to-background overlay (gradient → white/dark depending on theme) */}
      <rect x="0" y="0" width="600" height="280" fill="url(#utm-fade)" />
      <rect x="0" y="0" width="600" height="280" fill="url(#bg-fade)" />
    </svg>

    {/* ── Foreground content ── */}
    <div className="welcome-content">
      <div className="welcome-eyebrow">
        <span className="welcome-dot" />
        {t("welcome.eyebrow")}
      </div>

      <h1 className="welcome-title">
        {t("welcome.title.line1")}<br />
        <span style={{ color: "hsl(var(--brand-strong))" }}>{t("welcome.title.line2")}</span>
      </h1>

      {/* One claim at a time — see RotatingClaim for why this is not a grid */}
      <RotatingClaim />

      {/* Hint - arrow points RIGHT on desktop (donut is on the right), DOWN on mobile/tablet (donut is at the bottom) */}
      <div className="welcome-hint">
        <svg width="11" height="11" viewBox="0 0 10 10" aria-hidden="true" className="welcome-hint-arrow">
          <path
            d="M1 5 L9 5 M6 2 L9 5 L6 8"
            fill="none" stroke="currentColor" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
        {t("welcome.hint")}
      </div>
    </div>
  </div>
  );
};

export default WelcomePanel;
